import React, { FC, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { SxProps } from '@mui/system';

const enum IconPaths {
  MAIN_ICONS = 'icons',
}

interface IProps {
  icon: string;
  iconFn?: IconPaths;
  sx?: SxProps;
}

type SVGElement = FC<React.SVGProps<SVGSVGElement>>;

const iconModules = import.meta.glob<SVGElement>('../../../../assets/icons/*.svg', {
  import: 'default',
});

const iconCache: Record<string, SVGElement> = {};

const Icon = ({ icon, iconFn, ...rest }: IProps): JSX.Element | null => {
  const [SvgIcon, setSvgIcon] = useState<SVGElement | null>(null);

  useEffect(() => {
    let mounted = true;

    const loadIcon = async () => {
      if (!icon) {
        setSvgIcon(null);
        return;
      }

      if (iconCache[icon]) {
        setSvgIcon(() => iconCache[icon]);
        return;
      }

      const key = `../../../../assets/icons/${icon}.svg`;
      const importer = iconModules[key];

      if (!importer) {
        console.warn(`Icon not found: ${key}`);
        setSvgIcon(null);
        return;
      }

      try {
        const ImportedIcon = await importer();

        if (typeof ImportedIcon !== 'function') {
          console.error('Invalid SVG component:', icon, ImportedIcon);
          setSvgIcon(null);
          return;
        }

        iconCache[icon] = ImportedIcon;

        if (mounted) {
          setSvgIcon(() => ImportedIcon);
        }
      } catch (error) {
        console.error(`Failed to load icon "${icon}"`, error);
        setSvgIcon(null);
      }
    };

    loadIcon();

    return () => {
      mounted = false;
    };
  }, [icon, iconFn]);

  if (!SvgIcon) return null;

  return (
    <Box {...rest}>
      <SvgIcon className='m-svg' />
    </Box>
  );
};

export default Icon;
export { IconPaths };